export type WorkspaceBannerActionKey =
  | "reset"
  | "saveAsNew"
  | "saveCurrent"
  | "applyLatest";

export interface WorkspaceBannerAction {
  key: WorkspaceBannerActionKey;
  label: string;
}

export interface WorkspaceBannerCopy {
  testId: string;
  text: string;
  actions: WorkspaceBannerAction[];
}

export interface WorkspaceModalCopy {
  title: string;
  description: string;
  saveAsNewLabel: string;
  saveAsNewConfirmLabel: string;
  discardLabel: string;
  workspaceActionType?: "saveCurrent" | "applyLatest";
  saveCurrentWorkspaceLabel?: string;
  saveCurrentWorkspaceConfirmLabel?: string;
  saveCurrentWorkspaceWarning?: string;
  applyLatestTemplateLabel?: string;
}

export interface WorkspaceFlowInput {
  isDefault: boolean;
  isVivoTemplate: boolean;
  isSuperuserMode: boolean;
  hasDrift: boolean;
}

export interface WorkspaceBannerVisibilityInput {
  isDefault: boolean;
  hasDrift: boolean;
  isSwitchingWorkspace: boolean;
  currentWorkspaceId?: number | null;
  stateWorkspaceId?: number | null;
}

export function getWorkspaceBannerCopy({
  isDefault,
  isVivoTemplate,
  isSuperuserMode,
  hasDrift,
}: WorkspaceFlowInput): WorkspaceBannerCopy | null {
  if (hasDrift) {
    return {
      testId: "workspace-drift-banner",
      text: "This workspace has been updated. You can save your changes as a new workspace or apply the latest workspace.",
      actions: [
        {
          key: "saveAsNew",
          label: "Save as new workspace",
        },
        {
          key: "applyLatest",
          label: "Apply latest workspace",
        },
      ],
    };
  }

  if (isDefault) return null;

  const canSaveCurrent = !isVivoTemplate || isSuperuserMode;

  return {
    testId: "workspace-changes-banner",
    text: isVivoTemplate
      ? "You're currently editing a Vivo templated workspace. Save your changes in a new workspace or they will be lost."
      : "You're currently editing a workspace. Save your changes or they will be lost.",
    actions: [
      {
        key: "reset",
        label: "Reset",
      },
      {
        key: "saveAsNew",
        label: "Save as new",
      },
      ...(canSaveCurrent
        ? [
            {
              key: "saveCurrent" as const,
              label: "Save in current workspace",
            },
          ]
        : []),
    ],
  };
}

export function shouldShowWorkspaceBanner({
  isDefault,
  hasDrift,
  isSwitchingWorkspace,
  currentWorkspaceId,
  stateWorkspaceId,
}: WorkspaceBannerVisibilityInput): boolean {
  if (isSwitchingWorkspace) return false;
  if (hasDrift) return true;
  if (isDefault) return false;
  if (stateWorkspaceId === undefined || stateWorkspaceId === null) return true;
  if (currentWorkspaceId === undefined || currentWorkspaceId === null) {
    return false;
  }

  return stateWorkspaceId === currentWorkspaceId;
}

export function getWorkspaceModalCopy({
  isDefault,
  isVivoTemplate,
  isSuperuserMode,
  hasDrift,
}: WorkspaceFlowInput): WorkspaceModalCopy | null {
  if (hasDrift) {
    return {
      title: "This workspace has been updated",
      description:
        "Your changes are preserved. Save them as a new workspace or apply the latest workspace before leaving.",
      saveAsNewLabel: "Save as new",
      saveAsNewConfirmLabel: "Save changes",
      discardLabel: "Discard & leave",
      workspaceActionType: "applyLatest",
      applyLatestTemplateLabel: "Apply latest workspace",
    };
  }

  if (isDefault) return null;

  const canSaveCurrent = !isVivoTemplate || isSuperuserMode;

  return {
    title: "Unsaved workspace changes",
    description: isVivoTemplate
      ? "You have unsaved changes in this workspace. If you leave this page now, all your edits will be lost. Save your changes in a new workspace."
      : "You have unsaved changes in this workspace. If you leave this page now, all your edits will be lost. Save your changes in this current workspace or in a new workspace.",
    saveAsNewLabel: "Save as new",
    saveAsNewConfirmLabel: "Save changes",
    discardLabel: "Discard & leave",
    ...(canSaveCurrent
      ? {
          workspaceActionType: "saveCurrent" as const,
          saveCurrentWorkspaceLabel: "Save in current workspace",
          saveCurrentWorkspaceConfirmLabel: "Overwrite",
          saveCurrentWorkspaceWarning: isVivoTemplate
            ? "You are about to overwrite a Vivo templated workspace shared with all users. Changes will be distributed to everyone."
            : undefined,
        }
      : {}),
  };
}
